import React from 'react';
// import ReactEcharts from 'echarts-for-react';
import styled from 'styled-components';
import ReactEcharts from 'echarts-for-react';
import { Loader } from '@exxonmobil/react-unity';
import { EChartsOption } from 'echarts-for-react/src/types';
import { CostValidation } from '../../common/types/TrendsTypes';
import { ErrorBox } from '../../common/components/ErrorBox';
import { getNonProdData, getProdAndNonProdData, getProdData } from '../../common/utils/filterData';

interface Props {
  data: CostValidation[];
  isLoading: boolean;
  error: string;
  onRefresh: () => void;
  subscription: string;
}

const getResourcesByMonth = (data: CostValidation[]) => {
  const allResourceByMonth = new Map<string, CostValidation[]>();
  for (let i = 0; i < data.length; i += 1) {
    const resource = data[i];
    if (!allResourceByMonth.has(resource.date)) {
      allResourceByMonth.set(resource.date, []);
    }
    (allResourceByMonth.get(resource.date) as CostValidation[]).push(resource);
  }

  return allResourceByMonth;
};

const getTopResourcesByInitialMonth = (resourcesByDate: Map<string, CostValidation[]>, topAmount: number) => {
  const topResourcesInitialMonth: string[] = [];
  const initialMonthResources: CostValidation[] = resourcesByDate.values().next().value;
  if (initialMonthResources) {
    const sortedResources = initialMonthResources.slice().sort((a, b) => b.cost - a.cost);
    const topResourceCost = sortedResources.slice(0, topAmount);
    topResourceCost.forEach((resource) => topResourcesInitialMonth.push(resource.resourceName));
  }

  return topResourcesInitialMonth;
};

const getOthersCosts = (resourcesByDate: Map<string, CostValidation[]>, topResources: string[]) => {
  const otherCost: number[] = [];
  resourcesByDate.forEach((resourceList) => {
    let otherCostPerMonth: number = 0;
    resourceList.forEach((resource) => {
      if (!topResources.includes(resource.resourceName)) {
        otherCostPerMonth += resource.cost;
      }
    });
    otherCost.push(otherCostPerMonth);
  });
  return otherCost;
};

const getTotalCostsAllMonths = (topResourcesByDate: Map<string, CostValidation[]>) => {
  const allTotalCosts: number[] = [];

  topResourcesByDate.forEach((resourceListByDate) => {
    const totalCosts = resourceListByDate.reduce((acc, curr) => acc + curr.cost, 0);
    const roundedTotalCosts = parseFloat(totalCosts.toFixed(2));
    allTotalCosts.push(roundedTotalCosts);
  });

  return allTotalCosts;
};

const getTopResourceCosts = (topResourcesByDate: Map<string, CostValidation[]>, topResources: string[]) => {
  const costsPerResource = new Map<string, number[]>();

  topResources.forEach((resource) => {
    const costs: number[] = [];

    topResourcesByDate.forEach((resourceListByDate) => {
      const containResource = resourceListByDate.find((rs) => rs.resourceName === resource);
      if (containResource) {
        costs.push(containResource.cost);
      } else {
        costs.push(0);
      }
    });

    costsPerResource.set(resource, costs);
  });

  return costsPerResource;
};

const getResourcesSeriesBasesOnEnvData = (data: CostValidation[]) => {
  const allResourcesByMonth = getResourcesByMonth(data);
  const topResourcesByInitialMonth = getTopResourcesByInitialMonth(allResourcesByMonth, 6);
  const othersCosts = getOthersCosts(allResourcesByMonth, topResourcesByInitialMonth);
  const costsOfTopResourcesForAllMonths = getTopResourceCosts(allResourcesByMonth, topResourcesByInitialMonth);
  topResourcesByInitialMonth.push('Others');
  costsOfTopResourcesForAllMonths.set('Others', othersCosts);
  const totalCostsAllMonths = getTotalCostsAllMonths(allResourcesByMonth);

  const allResources = topResourcesByInitialMonth.map((resource) => ({
    name: resource,
    type: 'line',
    stack: 'Total',
    label: {
      show: false,
      position: 'top'
    },
    areaStyle: {},
    emphasis: {
      focus: 'series'
    },
    data: costsOfTopResourcesForAllMonths.get(resource)?.map((cost) => parseFloat(cost.toFixed(2))),
    color: resource === 'Others' ? 'grey' : undefined,
    showSymbol: true
  }));
  allResources.unshift({
    name: 'Total Cost',
    type: 'line',
    stack: '',
    label: {
      show: true,
      position: 'top'
    },
    areaStyle: {},
    emphasis: {
      focus: 'series'
    },
    data: totalCostsAllMonths,
    color: 'transparent',
    showSymbol: true
  });

  return allResources;
};

const DomainChart = ({ data, isLoading, error, onRefresh, subscription }: Props) => {
  if (error) {
    if (error === '401' || error === '403') {
      return <EmptyDataContainer>Sessions timeout, please close the page and login again.</EmptyDataContainer>;
    }
    return <ErrorBox onRefresh={onRefresh} isRefreshable />;
  }
  if (isLoading) {
    return (
      <EmptyDataContainer style={{ paddingBottom: '2rem' }}>
        <Loader data-testid="domain-cost-chart-loading" className="em-u-margin loader" />
      </EmptyDataContainer>
    );
  }
  if (data.length === 0) {
    return <EmptyDataContainer>No data available</EmptyDataContainer>;
  }

  let allResources;
  switch (subscription) {
    case 'NonProd':
      allResources = getResourcesSeriesBasesOnEnvData(getNonProdData(data));
      break;
    case 'Prod':
      allResources = getResourcesSeriesBasesOnEnvData(getProdData(data));
      break;
    case 'All':
    default:
      allResources = getResourcesSeriesBasesOnEnvData(getProdAndNonProdData(data));
  }
  const allMonths = [...new Set(data.map((costData) => costData.date))];

  const option: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      }
    },
    toolbox: {
      feature: {
        saveAsImage: {}
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        data: allMonths
      }
    ],
    yAxis: [
      {
        show: false,
        type: 'value'
      }
    ],
    series: allResources
  };

  return (
    <ReactEcharts opts={{ renderer: 'svg', width: 'auto', height: 300 }} option={option} style={{ height: '300px' }} />
  );
};

const EmptyDataContainer = styled.div`
  text-align: center;
  padding-top: 2rem;
  min-height: 5rem;
`;
export default DomainChart;
